import { v4 } from 'uuid';
import { action, computed, observable, runInAction, makeObservable } from 'mobx';

import {
    ALLOWED_FILES,
    buildManufacturingTypePayloadForStlPart,
    isStlFile,
    STL_MANUFACTURING_TYPE,
    restricted_file_extensions,
} from '@partsbadger/utils';

import QuoteStore from './QuoteStore';
import { createWorkerPool } from '../utils/workerPool';
import { matchPartFiles } from '../utils/partMatching';

import { message } from 'antd';

const { allowed_3d, allowed_2d } = ALLOWED_FILES;

/**
 * Check if a file is 2d of 3d file based on list of extensions
 * @param filename
 * @returns {string}
 */
export const is3dFile = (filename: string) => {
    const extension = filename.split('.').pop();
    return allowed_3d.find(ext => ext.toLowerCase() === extension?.toLowerCase());
};

export const is2dFile = (filename: string) => {
    const extension = filename.split('.').pop();
    return allowed_2d.find(ext => ext.toLowerCase() === extension?.toLowerCase());
};

export type FilePartType = '3D File' | '2D File' | 'Additional File';

export interface FileProps {
    [key: string]: any;
    index?: number;
    uid: string;
    name?: string;
    file?: File;
    '3D File'?: File | null;
    '2D File'?: File | null;
    'Additional File'?: File[];
    type?: FilePartType;
    similarity?: number;
    uploading?: boolean;
    progress?: number;
    /** Set when the last upload of this part failed; it stays in the list to retry or remove. */
    uploadError?: string;
    /** Set once the user moves, adds or removes a file; automatic matching leaves it alone. */
    locked?: boolean;
}

/**
 * Staff background uploads in flight at once. Each upload mostly waits on S3, so a few in
 * parallel cut the total time without loading the API (the customer flow already sends
 * every part at once).
 */
const MAX_PARALLEL_UPLOADS = 3;

export type PartUploadStatus = 'queued' | 'uploading' | 'completed' | 'failed';

/** A matched part waiting to be uploaded in the background (staff only). */
export interface PartUploadJob {
    uid: string;
    label: string;
    position: number;
    files: Pick<FileProps, '3D File' | '2D File' | 'Additional File'>;
    status: PartUploadStatus;
    progress: number;
    error?: string;
    /** The quote the part goes to and its email, fixed when queued: opening another quote must not move it. */
    quoteUid?: string;
    email?: string | null;
}

/** Where a part is created; the open quote when not given. */
interface UploadTarget {
    quoteUid?: string;
    email?: string | null;
}

const hasPartFiles = (item: FileProps) =>
    Boolean(item['3D File'] || item['2D File'] || item['Additional File']?.length);

const partLabel = (item: FileProps) =>
    item['3D File']?.name || item['2D File']?.name || item['Additional File']?.[0]?.name || item.name || 'Part';

class PartFiles {
    FILE_3D = '3D File';
    FILE_2D = '2D File';
    FILE_OTHERS = 'Additional File';

    /**
     * @type {Array} of objects :
     * @format [{
     *     id: unique_id
     *     file2d:
     *     file3:
     *     fileothers:
     *     similarity: 0 to 1, 1 is the exact name
     * }]
     */

    fileParts: Array<FileProps> = [];
    sortedBy: FilePartType | null = null;
    /** Last sort direction for the active `sortedBy` column. */
    sortOrder: 'asc' | 'desc' = 'asc';

    /** Staff background upload queue, shown in the upload tray. */
    uploadJobs: Array<PartUploadJob> = [];
    uploadQueueRunning = false;
    uploadTrayDismissed = false;

    constructor() {
        makeObservable(this, {
            fileParts: observable,
            sortedBy: observable,
            sortOrder: observable,
            // Actions
            setParts: action,
            isZipFile: action,
            addPart: action,
            removePart: action,
            addFile: action,
            removeFile: action,
            swapParts: action,
            swapParts2: action,
            uploadPart: action,
            hasParts: action,
            count: action,
            sortBy: action,
            uploadJobs: observable,
            uploadQueueRunning: observable,
            uploadTrayDismissed: observable,
            pendingUploadCount: computed,
            awaitingFirstPart: computed,
            enqueueUploads: action,
            retryFailedUploads: action,
            dismissUploads: action,
        });
    }

    /** Parts of a quote still queued or uploading in the staff tray. */
    pendingUploadCountFor(quoteUid?: string | null) {
        if (!quoteUid) return 0;
        return this.uploadJobs.filter(
            job => job.quoteUid === quoteUid && (job.status === 'queued' || job.status === 'uploading')
        ).length;
    }

    /** Parts of the staff background queue that have not finished yet. */
    get pendingUploadCount() {
        return this.uploadJobs.filter(job => job.status === 'queued' || job.status === 'uploading').length;
    }

    /**
     * True while several parts are being added and none of them has appeared yet: the first one
     * takes a while, so the form shows a placeholder until it lands. A single part, or a batch
     * that already added one, shows nothing.
     */
    get awaitingFirstPart() {
        return this.pendingUploadCount >= 2 && !this.uploadJobs.some(job => job.status === 'completed');
    }

    setParts(parts: Array<FileProps>) {
        runInAction(() => {
            this.fileParts = parts;
        });
    }

    isZipFile = (filename: string) => {
        return filename.split('.')?.pop()?.toLowerCase() === 'zip';
    };

    /** Add a dropped 3D or 2D file and pair it with the other files by name. */
    addPart = (file: File) => {
        const hasExtension = file.name.includes('.');
        const isAdditionalFileRestrictedForHobbyist =
            QuoteStore?.quote?.contact?.is_hobbyist && !is3dFile(file.name) && !is2dFile(file.name);
        const fileExtension = hasExtension ? file.name.split('.').pop()?.toLowerCase() : undefined;

        if (
            isAdditionalFileRestrictedForHobbyist ||
            !hasExtension ||
            (fileExtension && restricted_file_extensions.includes(fileExtension))
        ) {
            message.warning(
                fileExtension
                    ? `.${fileExtension} files are not allowed. Please try another file type.`
                    : `Files without an extension are not allowed. Please try another file type.`,
                4
            );
            return;
        }

        // Exclude Additional Files
        if (!is3dFile(file.name) && !is2dFile(file.name)) {
            message.warning(`Only 3D and 2D files are allowed. ${file.name} will not be added.`, 4);
            return;
        }

        this.arrivalOrder.set(file, this.nextArrival++);
        this.rematchParts(file);
    };

    /** Order in which files were added, so new parts appear in drop order. */
    private arrivalOrder = new WeakMap<File, number>();
    private nextArrival = 0;

    /**
     * Re-pair every automatically matched 3D and 2D file together with the new one, so the
     * result does not depend on the order the files arrive in. Parts the user edited stay as
     * they are, parts keep their place in the list, and a part keeps its uid while its 3D
     * (or, without one, its 2D) file stays in it.
     */
    private rematchParts(added: File) {
        const auto = this.fileParts.filter(part => !part.locked && !part.uploading && !part['Additional File']?.length);
        const files = [
            ...auto.flatMap(part => [part['3D File'], part['2D File']]),
            added,
        ].filter((file): file is File => Boolean(file));
        const files3d = files.filter(file => is3dFile(file.name));
        const files2d = files.filter(file => !is3dFile(file.name));

        const pairs = matchPartFiles(files3d, files2d, file => file.name);
        const paired = new Set(pairs.flatMap(pair => [pair.file3d, pair.file2d]));
        const groups: Array<{ '3D File': File | null; '2D File': File | null; similarity: number }> = [
            ...pairs.map(pair => ({ '3D File': pair.file3d, '2D File': pair.file2d, similarity: pair.score })),
            ...files3d
                .filter(file => !paired.has(file))
                .map(file => ({ '3D File': file, '2D File': null, similarity: 0 })),
            ...files2d
                .filter(file => !paired.has(file))
                .map(file => ({ '3D File': null, '2D File': file, similarity: 0 })),
        ];

        const placeOf = new Map<File, number>();
        this.fileParts.forEach((part, index) => {
            [part['3D File'], part['2D File']].forEach(file => file && placeOf.set(file, index));
        });
        const place = (file: File | null) =>
            !file ? Infinity : placeOf.get(file) ?? this.fileParts.length + (this.arrivalOrder.get(file) ?? 0);

        const usedUids = new Set<string>();
        const uidFor = (file: File | null) => {
            const uid = file && auto.find(part => part['3D File'] === file || part['2D File'] === file)?.uid;
            return uid && !usedUids.has(uid) ? uid : undefined;
        };
        const rebuilt = groups.map(group => {
            const uid = uidFor(group['3D File']) ?? uidFor(group['2D File']) ?? v4();
            usedUids.add(uid);
            return { uid, ...group, uploading: false, progress: 0 } as FileProps;
        });

        const ranked = [
            ...this.fileParts
                .filter(part => !auto.includes(part))
                .map(part => ({ part, rank: this.fileParts.indexOf(part) })),
            ...rebuilt.map(part => ({
                part,
                rank: Math.min(place(part['3D File'] ?? null), place(part['2D File'] ?? null)),
            })),
        ];
        this.setParts(ranked.sort((a, b) => a.rank - b.rank).map(({ part }) => part));
    }

    private lock(...parts: Array<FileProps | undefined>) {
        parts.forEach(part => {
            if (part) part.locked = true;
        });
    }

    removePart(item: FileProps) {
        this.setParts([...this.fileParts.filter(i => i.uid !== item.uid)]);
    }

    addFile(index: number, fileType: FilePartType, file: File) {
        this.lock(this.fileParts[index]);
        if (fileType === 'Additional File') {
            const files = this.fileParts[index][fileType] || [];

            this.fileParts[index][fileType] = [...files, file];
        } else {
            this.fileParts[index][fileType] = file;
        }
    }

    removeFile(item: FileProps, fileType: FilePartType, file?: File) {
        this.setParts([
            ...this.fileParts.filter(i => {
                if (i.uid !== item.uid) {
                    return i;
                }

                if (fileType === 'Additional File') {
                    if (file) {
                        i[fileType] = i[fileType]?.filter((f: File) => f.name !== file.name);
                    }
                } else {
                    i[fileType] = null;
                }

                return i;
            }),
        ]);
    }

    /**
     * Swap a part file
     * @param index_from: index from the file was picked
     * @param index_to: index where the file was dropped
     * @param FileTypeFrom: Spot file type where the file was picker
     * @param FileTypeTo: Spot File type were the file is dropped
     * @param file : file object
     */
    swapParts(index_from: number, index_to: number, FileTypeFrom: FilePartType, FileTypeTo: FilePartType, file: File) {
        this.lock(this.fileParts[index_from], this.fileParts[index_to]);
        if (FileTypeTo === 'Additional File' && (FileTypeFrom === '2D File' || FileTypeFrom === '3D File')) {
            // Remove the file from the current position 2d or 3d
            this.fileParts[index_from][FileTypeFrom] = null;
            // Append file to the other files
            const current_other_files = this.fileParts[index_to]['Additional File'] || [];
            this.fileParts[index_to]['Additional File'] = [...current_other_files, file];
        } else if (FileTypeFrom === 'Additional File' && (FileTypeTo === '2D File' || FileTypeTo === '3D File')) {
            // Remove the file from file others on the previous position
            this.fileParts[index_from]['Additional File'] = (
                this.fileParts[index_from]['Additional File'] || []
            ).filter((f: File) => f.name !== file.name);

            // save if there is a file in the destiny spot
            const current = this.fileParts[index_to][FileTypeTo];

            // Move to the other files if there was a file
            if (current) {
                const current_other_files = this.fileParts[index_from]['Additional File'] || [];
                this.fileParts[index_from]['Additional File'] = [...current_other_files, current];
            }

            // Put the file on the 2d or 3d spot
            this.fileParts[index_to][FileTypeTo] = file;
        } else if (FileTypeFrom === 'Additional File' && FileTypeTo === 'Additional File') {
            // Remove the file from file others on the previous position, filtering by name
            this.fileParts[index_from]['Additional File'] = (
                this.fileParts[index_from]['Additional File'] || []
            ).filter((f: File) => f.name !== file.name);

            const current_other_files = this.fileParts[index_to]['Additional File'] || [];
            this.fileParts[index_to]['Additional File'] = [...current_other_files, file];
        } else {
            const fromPart = this.fileParts[index_from] as any;
            const toPart = this.fileParts[index_to] as any;
            const file_from = fromPart[FileTypeFrom];
            const file_to = toPart[FileTypeTo];

            toPart[FileTypeTo] = file_from;
            fromPart[FileTypeFrom] = file_to;
        }
    }

    /**
     * Swap a part file
     * @param index_from
     * @param index_to
     * @param fileType type of file
     * @param file
     */
    swapParts2(index_from: number, index_to: number, fileType: FilePartType, file: File) {
        this.lock(this.fileParts[index_from], this.fileParts[index_to]);
        if (fileType === 'Additional File') {
            const destination_files = this.fileParts[index_to][fileType];
            const origin_files = this.fileParts[index_from][fileType];

            // Append to the destination files
            this.fileParts[index_to][fileType] = destination_files?.length ? [...destination_files, file] : [file];
            // Remove from the original position
            this.fileParts[index_from][fileType] = origin_files?.filter((f: File) => {
                return f.name !== file.name;
            });
        } else {
            const file_to = this.fileParts[index_to][fileType];
            this.fileParts[index_to][fileType] = this.fileParts[index_from][fileType];
            this.fileParts[index_from][fileType] = file_to;
        }
    }

    /**
     * Upload one matched part and create its line item. Rejects when the line item was
     * not created, so callers can surface the failure (newLineItemWithFiles swallows it).
     */
    private submitPartFiles(
        files: Pick<FileProps, '3D File' | '2D File' | 'Additional File'>,
        position: number,
        onProgress?: (percent: number) => void,
        target?: UploadTarget
    ) {
        const file3d = files['3D File'];
        const formData = new FormData();

        if (file3d) {
            formData.append('file_3d', file3d);
        }

        if (files['2D File']) {
            formData.append('file_2d', files['2D File']);
        }

        files['Additional File']?.forEach((f: File) => formData.append('file_others', f));

        // Send the exact row position of the file in the drag and drop
        formData.append('position', position.toString());

        const email = target ? target.email : QuoteStore.quote?.email;
        if (email) {
            formData.append('email', email);
        }

        const onUploadProgress = (progressEvent: any) => {
            if (!onProgress || !progressEvent?.total) return;
            onProgress(Math.round((progressEvent.loaded / progressEvent.total) * 100));
        };

        return QuoteStore.newLineItemWithFiles(formData, onUploadProgress, target?.quoteUid).then(async data => {
            if (!data?.id) {
                throw new Error('Upload failed');
            }
            if (file3d && isStlFile(file3d.name) && data.manufacturing_type !== STL_MANUFACTURING_TYPE) {
                try {
                    const payload = buildManufacturingTypePayloadForStlPart(data, STL_MANUFACTURING_TYPE);
                    if (target?.quoteUid && target.quoteUid !== QuoteStore.quote_uid) {
                        // The part belongs to a quote that is no longer open: update it there.
                        await QuoteStore.updateLineItemOfQuote(target.quoteUid, payload);
                    } else {
                        await QuoteStore.updateLineItem(payload);
                    }
                } catch (e) {
                    // The line item already exists; do not report the part as failed.
                    console.error(e);
                }
            }
            return data;
        });
    }

    /** Update a pending part by uid: indexes shift as other parts finish and are removed. */
    private updatePart(uid: string, changes: Partial<FileProps>) {
        runInAction(() => {
            const part = this.fileParts.find(i => i.uid === uid);
            if (part) {
                Object.assign(part, changes);
            }
        });
    }

    uploadPart(item: FileProps) {
        const position = this.fileParts.findIndex(i => i.uid === item.uid) + 1;

        this.updatePart(item.uid, { uploading: true, uploadError: undefined });

        this.submitPartFiles(item, position, percent => this.updatePart(item.uid, { progress: percent }))
            .then(() => {
                this.removePart(item);
            })
            .catch(error => {
                console.error(error);
                // Keep the part so the customer can retry with Continue or remove it.
                this.updatePart(item.uid, { uploading: false, progress: 0, uploadError: 'Upload failed' });
                message.error(`We couldn't upload ${partLabel(item)}. Please try again or remove it.`, 6);
            });
    }

    /**
     * Staff flow: queue the matched parts and close the match modal right away. Parts
     * upload a few at a time, starting in their drag-and-drop order, while the user keeps
     * working. Each keeps its drag-and-drop position whatever order they finish in.
     */
    enqueueUploads(items: Array<FileProps>) {
        // Fixed now: the quote and email the parts belong to, whatever is opened meanwhile.
        const quoteUid = QuoteStore.quote_uid;
        const email = QuoteStore.quote?.email ?? null;
        const jobs: Array<PartUploadJob> = items
            .map((item, index) => ({ item, position: index + 1 }))
            .filter(({ item }) => hasPartFiles(item))
            .map(({ item, position }) => ({
                uid: item.uid,
                label: partLabel(item),
                position,
                files: {
                    '3D File': item['3D File'],
                    '2D File': item['2D File'],
                    'Additional File': item['Additional File'],
                },
                status: 'queued' as PartUploadStatus,
                progress: 0,
                quoteUid,
                email,
            }));

        this.fileParts = [];

        if (!jobs.length) return;

        const idle = !this.uploadJobs.some(job => job.status === 'queued' || job.status === 'uploading');
        // Start a fresh batch once the previous one finished, but keep the failures of this
        // quote to retry: another quote's failures would be retried against this one.
        this.uploadJobs = [
            ...(idle
                ? this.uploadJobs.filter(job => job.status === 'failed' && job.quoteUid === quoteUid)
                : this.uploadJobs),
            ...jobs,
        ];
        this.uploadTrayDismissed = false;
        this.processUploadQueue();
    }

    /** Claims the next queued job synchronously so two workers never take the same one. */
    private takeNextUpload() {
        const job = this.uploadJobs.find(candidate => candidate.status === 'queued');
        if (!job) return undefined;
        runInAction(() => {
            job.status = 'uploading';
            job.progress = 0;
            job.error = undefined;
        });
        return job;
    }

    private uploadPool = createWorkerPool(
        () => this.takeNextUpload(),
        job => this.uploadJob(job),
        MAX_PARALLEL_UPLOADS
    );

    /** Starts any missing upload workers, so parts queued while others upload run in parallel too. */
    processUploadQueue() {
        runInAction(() => {
            this.uploadQueueRunning = true;
        });
        this.uploadPool.kick();
        void this.uploadPool.idle().then(() => {
            if (this.uploadPool.active === 0) {
                runInAction(() => {
                    this.uploadQueueRunning = false;
                });
            }
        });
    }

    private async uploadJob(job: PartUploadJob) {
        try {
            await this.submitPartFiles(
                job.files,
                job.position,
                percent =>
                    runInAction(() => {
                        // Keep 100% for when the line item is actually created.
                        job.progress = Math.min(percent, 99);
                    }),
                { quoteUid: job.quoteUid, email: job.email }
            );
            runInAction(() => {
                job.status = 'completed';
                job.progress = 100;
            });
        } catch (error) {
            runInAction(() => {
                job.status = 'failed';
                job.error = error instanceof Error ? error.message : 'Upload failed';
            });
        }
    }

    retryFailedUploads() {
        this.uploadJobs.forEach(job => {
            if (job.status === 'failed') {
                job.status = 'queued';
                job.progress = 0;
                job.error = undefined;
            }
        });
        this.uploadTrayDismissed = false;
        this.processUploadQueue();
    }

    dismissUploads() {
        if (this.uploadJobs.some(job => job.status === 'queued' || job.status === 'uploading')) return;
        this.uploadJobs = [];
        this.uploadTrayDismissed = true;
    }

    hasParts() {
        return this.fileParts.length > 0;
    }

    count() {
        return this.fileParts.length;
    }

    sortBy(fileType: FilePartType) {
        if (this.sortedBy === fileType) {
            this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortedBy = fileType;
            this.sortOrder = 'asc';
        }

        const direction = this.sortOrder === 'asc' ? 1 : -1;

        this.fileParts = [...this.fileParts].sort((item1, item2) => {
            const fileA = item1[fileType] as File | null | undefined;
            const fileB = item2[fileType] as File | null | undefined;
            const nameA = fileA?.name ?? '';
            const nameB = fileB?.name ?? '';

            const emptyA = !nameA;
            const emptyB = !nameB;
            if (emptyA && emptyB) {
                return 0;
            }
            if (emptyA) {
                return 1;
            }
            if (emptyB) {
                return -1;
            }

            const cmp = nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
            return direction * cmp;
        });
    }
}

const partStore = new PartFiles();

export default partStore;
